import { API_ERROR_MESSAGES, ApiError, apiErrorFromResponse } from "./ApiError";
import { notifySessionEnded, waitForAuthBridge, type AuthBridge } from "./authBridge";
import { appendQuery, type QueryParams } from "./buildPath";

/** Префикс API на origin клиента: прокси Vite в dev, nginx в e2e и production. Сервер на CORS не отвечает. */
export const API_BASE_PATH = "/api";

/** Таймаут ожидания ответа (до заголовков); чтение тела `raw`-ответа он не ограничивает. */
export const DEFAULT_TIMEOUT_MS = 30_000;

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

export interface RequestOptions {
    /** Query-параметры, см. `buildQuery`. Добавляются и к пути, где `?` уже есть. */
    query?: QueryParams;
    /** JSON-тело или файл (`Blob`/`File` уходит как есть с `Content-Type` файла). */
    body?: unknown;
    /** Отмена: `signal` из `queryFn` React Query или свой `AbortController`. */
    signal?: AbortSignal;
    headers?: HeadersInit;
    /**
     * `false` — без `Authorization`, без ожидания `authReady` и без refresh по 401:
     * `login`, `register`, `isauthorized`, `password-reset/*`.
     */
    auth?: boolean | "optional";
    /** Validate the caller's account/board context after auth readiness and before each attempt. */
    assertContext?: () => void;
    /** `null` — без таймаута (большие файлы). */
    timeoutMs?: number | null;
}

/** URL API относительно origin: `/v1/users/1/avatar?v=2` -> `/api/v1/users/1/avatar?v=2`. */
export function apiUrl(path: string): string {
    return `${API_BASE_PATH}${path.startsWith("/") ? path : `/${path}`}`;
}

function absoluteUrl(path: string): string {
    // SPA: данные загружаются только в браузере (AGENTS.md, «Правила»).
    if (typeof window === "undefined") {
        throw new Error("HTTP-клиент API вызывается только в браузере");
    }
    return new URL(apiUrl(path), window.location.origin).href;
}

function abortReason(signal: AbortSignal): unknown {
    return signal.reason ?? new DOMException("Запрос отменён", "AbortError");
}

/** Ждать промис, пока не отменён `signal`. */
function abortable<T>(promise: Promise<T>, signal: AbortSignal | undefined): Promise<T> {
    if (!signal) return promise;
    if (signal.aborted) return Promise.reject(abortReason(signal));
    return new Promise<T>((resolve, reject) => {
        const onAbort = () => reject(abortReason(signal));
        signal.addEventListener("abort", onAbort, { once: true });
        promise.then(resolve, reject).finally(() => signal.removeEventListener("abort", onAbort));
    });
}

function anySignal(signals: AbortSignal[]): AbortSignal {
    if (typeof AbortSignal.any === "function") return AbortSignal.any(signals);

    const controller = new AbortController();
    for (const signal of signals) {
        if (signal.aborted) {
            controller.abort(signal.reason);
            break;
        }
        signal.addEventListener("abort", () => controller.abort(signal.reason), { once: true });
    }
    return controller.signal;
}

const BLOB_TAGS = new Set(["[object Blob]", "[object File]"]);

function isBlob(body: unknown): body is Blob {
    // По тегу, а не только instanceof: файл может прийти из другого realm (iframe, jsdom в тестах).
    return (typeof Blob !== "undefined" && body instanceof Blob) || BLOB_TAGS.has(Object.prototype.toString.call(body));
}

interface PreparedRequest {
    url: string;
    method: HttpMethod;
    headers: Headers;
    body: BodyInit | undefined;
}

function prepare(method: HttpMethod, path: string, options: RequestOptions, raw: boolean): PreparedRequest {
    const headers = new Headers();
    if (!raw) headers.set("Accept", "application/json");

    let body: BodyInit | undefined;
    if (isBlob(options.body)) {
        body = options.body;
        headers.set("Content-Type", options.body.type || "application/octet-stream");
    } else if (options.body !== undefined) {
        body = JSON.stringify(options.body);
        headers.set("Content-Type", "application/json");
    }

    new Headers(options.headers).forEach((value, key) => headers.set(key, value));

    return { url: absoluteUrl(appendQuery(path, options.query)), method, headers, body };
}

async function send(
    prepared: PreparedRequest,
    token: string | null,
    { signal, timeoutMs = DEFAULT_TIMEOUT_MS }: RequestOptions,
): Promise<Response> {
    const headers = new Headers(prepared.headers);
    if (token) headers.set("Authorization", `Bearer ${token}`);

    // Свой таймер, а не AbortSignal.timeout: его нужно снять после заголовков,
    // иначе таймаут оборвёт долгое скачивание `raw`-ответа.
    const timeout = timeoutMs ? new AbortController() : null;
    const timer =
        timeout && timeoutMs
            ? setTimeout(() => timeout.abort(new DOMException("Таймаут запроса", "TimeoutError")), timeoutMs)
            : undefined;
    const signals = [signal, timeout?.signal].filter((item): item is AbortSignal => item !== undefined);

    try {
        return await fetch(prepared.url, {
            method: prepared.method,
            headers,
            body: prepared.body,
            credentials: "same-origin",
            signal: signals.length ? anySignal(signals) : undefined,
        });
    } catch (error) {
        if (signal?.aborted) throw error;
        if (timeout?.signal.aborted) throw ApiError.timeout(error);
        throw ApiError.network(error);
    } finally {
        clearTimeout(timer);
    }
}

async function readError(response: Response): Promise<ApiError> {
    const body = await response.text().catch(() => "");
    return apiErrorFromResponse(response.status, body);
}

async function readJson(response: Response): Promise<unknown> {
    let text: string;
    try {
        text = await response.text();
    } catch (error) {
        throw ApiError.network(error);
    }
    if (!text) return undefined;
    try {
        return JSON.parse(text);
    } catch (error) {
        throw new ApiError({
            status: response.status,
            code: "invalid_response",
            message: API_ERROR_MESSAGES.invalidResponse,
            cause: error,
        });
    }
}

async function readResponse(response: Response, raw: boolean): Promise<unknown> {
    // 304 — ответ на If-None-Match (аватары): тело берётся из своего кэша.
    if (raw && (response.ok || response.status === 304)) return response;
    if (!response.ok) throw await readError(response);
    return readJson(response);
}

let refreshFlight: Promise<string | null> | null = null;

/**
 * Новый токен после 401. Single-flight: параллельные 401 ждут один refresh. Если токен уже сменил
 * другой запрос, пока этот был в сети, — refresh не нужен. Ошибка сети при refresh пробрасывается:
 * это не конец сессии.
 */
function renewToken(session: AuthBridge, rejectedToken: string): Promise<string | null> {
    const current = session.getAccessToken();
    if (current && current !== rejectedToken) return Promise.resolve(current);

    refreshFlight ??= session
        .refresh()
        .then((ok) => (ok ? session.getAccessToken() : null))
        .finally(() => {
            refreshFlight = null;
        });
    return refreshFlight;
}

function unauthorized(): ApiError {
    return new ApiError({ status: 401, message: API_ERROR_MESSAGES[401] });
}

/**
 * Авторизованная отправка: барьер `authReady`, токен, при 401 — один refresh и повтор. `sendWith` — транспорт
 * (`fetch` у `request`, `XMLHttpRequest` у `upload`).
 */
async function withAuthorization<R extends { status: number }>(
    signal: AbortSignal | undefined,
    sendWith: (token: string) => Promise<R>,
    assertContext?: () => void,
    optional = false,
): Promise<R> {
    // Барьер: до подключения сессии и ответа первого isauthorized запрос не уходит.
    const session = await abortable(waitForAuthBridge(), signal);
    await abortable(session.ready(), signal);
    assertContext?.();

    const token = session.getAccessToken();
    if (!token) {
        if (!optional) throw unauthorized();
        const context = session.getContextKey?.();
        const response = await sendWith("");
        assertContext?.();
        if (context !== session.getContextKey?.()) throw new DOMException("Account changed", "AbortError");
        return response;
    }
    const context = session.getContextKey?.();
    const checkContext = () => {
        assertContext?.();
        if (context !== session.getContextKey?.()) throw new DOMException("Account changed", "AbortError");
    };

    const response = await sendWith(token);
    checkContext();
    if (response.status !== 401) return response;

    const renewed = await abortable(renewToken(session, token), signal);
    checkContext();
    if (!renewed) {
        notifySessionEnded();
        throw unauthorized();
    }

    const retried = await sendWith(renewed);
    checkContext();
    if (retried.status === 401) {
        notifySessionEnded();
        throw unauthorized();
    }
    return retried;
}

function sendAuthorized(prepared: PreparedRequest, options: RequestOptions): Promise<Response> {
    return withAuthorization(
        options.signal,
        (token) => send(prepared, token, options),
        options.assertContext,
        options.auth === "optional",
    );
}

/**
 * Запрос к API сервера. Путь — как в OpenAPI (`/v1/...`), параметры пути — через `buildPath`.
 * Ошибки — `ApiError`; отмена через `signal` пробрасывает `AbortError` как есть.
 *
 * @example
 * request<Client>("GET", buildPath("/v1/clients/{clientId}", { clientId }), { signal });
 * request<Attachment>("POST", buildPath("/v1/cards/{cardId}/attachments", { cardId }), {
 *     query: { fileName: file.name },
 *     body: file,
 *     timeoutMs: null,
 * });
 * const response = await request("GET", path, { raw: true }); // скачивание: await response.blob()
 */
export function request<T>(method: HttpMethod, path: string, options?: RequestOptions & { raw?: false }): Promise<T>;
export function request(method: HttpMethod, path: string, options: RequestOptions & { raw: true }): Promise<Response>;
export async function request(
    method: HttpMethod,
    path: string,
    { raw = false, ...options }: RequestOptions & { raw?: boolean } = {},
): Promise<unknown> {
    const prepared = prepare(method, path, options, raw);
    const response =
        options.auth === false ? await send(prepared, null, options) : await sendAuthorized(prepared, options);
    return readResponse(response, raw);
}

export interface UploadOptions {
    query?: QueryParams;
    /** Дополнительные заголовки (`X-Content-SHA256`). */
    headers?: HeadersInit;
    /** Тип тела; по умолчанию — тип файла или `application/octet-stream`. */
    contentType?: string;
    signal?: AbortSignal;
    /** Прогресс отправки тела: отправлено и всего байт. После 401 и refresh отсчёт начинается заново. */
    onProgress?: (loaded: number, total: number) => void;
}

interface XhrResponse {
    status: number;
    text: string;
}

function sendXhr(
    method: HttpMethod,
    url: string,
    body: Blob,
    headers: Headers,
    token: string,
    { signal, onProgress }: UploadOptions,
): Promise<XhrResponse> {
    return new Promise((resolve, reject) => {
        if (signal?.aborted) {
            reject(abortReason(signal));
            return;
        }
        const xhr = new XMLHttpRequest();
        const onAbort = () => xhr.abort();
        xhr.open(method, url);
        headers.forEach((value, key) => xhr.setRequestHeader(key, value));
        xhr.setRequestHeader("Authorization", `Bearer ${token}`);
        xhr.upload.addEventListener("progress", (event) =>
            onProgress?.(event.loaded, event.lengthComputable ? event.total : body.size),
        );
        xhr.addEventListener("load", () => resolve({ status: xhr.status, text: xhr.responseText }));
        xhr.addEventListener("error", () => reject(ApiError.network()));
        xhr.addEventListener("abort", () =>
            reject(signal ? abortReason(signal) : new DOMException("Запрос отменён", "AbortError")),
        );
        xhr.addEventListener("loadend", () => signal?.removeEventListener("abort", onAbort));
        signal?.addEventListener("abort", onAbort, { once: true });
        xhr.send(body);
    });
}

/**
 * Загрузка файла сырым телом (вложения) с прогрессом: `fetch` прогресс отправки не даёт, поэтому — `XMLHttpRequest`.
 * Авторизация, refresh по 401 и ошибки — как у `request`; таймаута нет (большие файлы), отмена — `signal`.
 *
 * @example
 * upload<Attachment>("POST", buildPath("/v1/cards/{cardId}/attachments", { cardId }), file, {
 *     query: { fileName: file.name, kind: "card" },
 *     onProgress: (loaded, total) => setProgress(loaded / total),
 * });
 */
export async function upload<T>(method: HttpMethod, path: string, file: Blob, options: UploadOptions = {}): Promise<T> {
    const url = absoluteUrl(appendQuery(path, options.query));
    const headers = new Headers({ Accept: "application/json" });
    headers.set("Content-Type", options.contentType || file.type || "application/octet-stream");
    new Headers(options.headers).forEach((value, key) => headers.set(key, value));

    const response = await withAuthorization(options.signal, (token) =>
        sendXhr(method, url, file, headers, token, options),
    );
    if (response.status < 200 || response.status >= 300) throw apiErrorFromResponse(response.status, response.text);
    if (!response.text) return undefined as T;
    try {
        return JSON.parse(response.text) as T;
    } catch (error) {
        throw new ApiError({
            status: response.status,
            code: "invalid_response",
            message: API_ERROR_MESSAGES.invalidResponse,
            cause: error,
        });
    }
}

/** Сброс single-flight между тестами. */
export function resetHttpClientForTests(): void {
    refreshFlight = null;
}
